import { FormatMessageResponse } from "stream-chat";
import { ChatChannel, ChatChannelType } from "../models/ChatChannel";
import { ChatChannelEvent } from "../models/ChatChannelEvent";
import { ChatChannelGroup } from "../models/ChatChannelGroup";
import { ChatChannelMessage } from "../models/ChatChannelMessage";
import { ChatChannelUnknown } from "../models/ChatChannelUnknown";
import { ChatChannelUser } from "../models/ChatChannelUser";
import { ChannelExtended } from "../pages/chat/components/ChatListTile";
import { FriendshipStatus, FriendshipStatusType } from "../models/FriendshipStatus";

export const createChatChannelMessageModel = (message: FormatMessageResponse) : ChatChannelMessage => {
  return new ChatChannelMessage({
    id: message.id,
    author: {
      id: message.user?.id || '',
      name: message.user?.name || '',
    },
    text: message.text || '',
    attachments: message.attachments?.map((attachment) => ({
      type: attachment.type || '',
    })) || [],
    date: message.created_at,
  });
}

const getFriendshipStatus = (channel: ChannelExtended, currentUserId: string) : FriendshipStatus => {
  if (channel.type === ChatChannelType.Messaging) {
    const status = channel.data?.status as string | undefined;
    
    switch (status) {
      case 'established':
        return new FriendshipStatus(FriendshipStatusType.Established, false);
      case 'pending': {
        const channelState = channel.state;
        if (channelState) {
          const isUnblocked = channelState.messages.length > 1 ||
            channelState.messages.some(message => 
              message.user?.id !== channel.data.created_by?.id
            );

          if (currentUserId === channel.data.created_by?.id) {
            return new FriendshipStatus(FriendshipStatusType.RequestSent, !isUnblocked);
          } else {
            return new FriendshipStatus(FriendshipStatusType.RequestReceived, !isUnblocked);
          }
        }
        return new FriendshipStatus(FriendshipStatusType.Foreign, false);
      }
      default:
        return new FriendshipStatus(FriendshipStatusType.Foreign, false);
    }
  }
  return new FriendshipStatus(FriendshipStatusType.Established, false);
}

export const createChatChannelModel = (channel: ChannelExtended, currentUserId: string) : ChatChannel => {
    const latestMessages = channel.state?.latestMessages;
    
    let chatChannelData = {
      id: channel.data?.id || '',
      name: channel.data?.name || '',
      image: channel.data?.image || '',
      isMuted: channel.initialized ? channel.muteStatus().muted || false : false,
      memberCount: channel.data?.member_count || 0,
      unreadCount: channel.state?.unreadCount || 0,
      lastMessage: latestMessages.length > 0 ? createChatChannelMessageModel(latestMessages[latestMessages.length - 1]) : null,
      friendshipStatus: getFriendshipStatus(channel, currentUserId),
      channel: channel,
      type: null
    }

    switch (channel.type) {
      case ChatChannelType.Messaging: {
        const member = Object.values(channel.state?.members)
          .find((member: { user: { id: string | undefined; }; }) => member.user.id !== currentUserId)?.user;

        if (member) {
          if(member.image)
          {
            // Delete query string from image url to cache properly
            let urlObj = new URL(member.image);
            member.image = urlObj.origin + urlObj.pathname;
          }

          return new ChatChannelUser({
            ...chatChannelData,
            type: ChatChannelType.Messaging,
            id: member.id,
            name: member.name,
            image: member.image,
            slug: member.slug,
            badge: member.badge,
            merchant: member.merchant || false,
          });
        }
        break;
      }
      case ChatChannelType.Event:
        return new ChatChannelEvent({
            ...chatChannelData,
            type: ChatChannelType.Event,
            id: channel.data?.id || '',
        });
      case ChatChannelType.Group:
        const isAdmin = channel.data.admins?.includes(currentUserId);
        const adminCount = channel.data.admins?.length ?? 0;

        return new ChatChannelGroup({
            ...chatChannelData,
            type: ChatChannelType.Group,
            id: channel.data?.id || '',
            isAdmin: isAdmin || false,
            adminCount: adminCount || 0
        });
    }
    return new ChatChannelUnknown({
        ...chatChannelData,
        id: channel.data?.id || '',
    });
  }